import { ReactNode } from "react";
import { ChevronDown } from "lucide-react";
import {
  Button,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  V2Loader,
  cn,
} from "web-design-system";

export function ReviewSection({
  title,
  defaultOpen = false,
  children,
}: {
  title: string;
  defaultOpen?: boolean;
  children: ReactNode;
}) {
  return (
    <Collapsible
      defaultOpen={defaultOpen}
      className="group flex flex-col rounded-2xl bg-card p-4"
    >
      <CollapsibleTrigger asChild>
        <div className="flex items-center justify-between gap-4">
          <h5>{title}</h5>
          <Button
            variant="outline"
            size="icon"
            aria-label={`Toggle ${title.toLowerCase()}`}
          >
            <ChevronDown className="transition-transform duration-200 group-data-[state=open]:rotate-180" />
          </Button>
        </div>
      </CollapsibleTrigger>
      <CollapsibleContent className="mt-4">
        <div className="flex flex-col gap-6 rounded-lg bg-background p-4">
          {children}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

export function ReviewBlockState({
  isLoading,
  isError,
  isEmpty,
  emptyMessage,
  emptyState,
  errorMessage = "This section could not be loaded.",
  className,
  children,
}: {
  isLoading: boolean;
  isError?: boolean;
  isEmpty?: boolean;
  emptyMessage?: string;
  // Replaces the plain empty notice, e.g. with a `ReviewEmptyState`.
  emptyState?: ReactNode;
  errorMessage?: string;
  className?: string;
  children: ReactNode;
}) {
  let content = children;
  if (isError) {
    content = <ReviewNotice tone="error">{errorMessage}</ReviewNotice>;
  } else if (isEmpty) {
    content = emptyState ?? <ReviewNotice>{emptyMessage}</ReviewNotice>;
  }
  return (
    <V2Loader
      isLoading={isLoading}
      className={cn("flex min-h-24 flex-col gap-6", className)}
    >
      {content}
    </V2Loader>
  );
}

export function ReviewNotice({
  tone = "neutral",
  className,
  children,
}: {
  tone?: "neutral" | "positive" | "warning" | "error";
  className?: string;
  children: ReactNode;
}) {
  return (
    <div
      className={cn(
        "rounded-lg p-4 text-sm font-semibold",
        tone === "neutral" && "bg-tag-slate text-tag-slate-foreground",
        tone === "positive" && "bg-tag-green text-tag-green-foreground",
        tone === "warning" && "bg-tag-yellow text-tag-yellow-foreground",
        tone === "error" && "bg-tag-red text-tag-red-foreground",
        className
      )}
    >
      {children}
    </div>
  );
}

export function ReviewEmptyState({
  icon,
  tone = "neutral",
  children,
}: {
  icon: ReactNode;
  tone?: "neutral" | "positive";
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center gap-4 py-12 text-center">
      <span
        className={cn(
          "flex size-18 items-center justify-center rounded-full [&_svg]:size-6",
          tone === "neutral" && "bg-muted text-foreground",
          tone === "positive" && "bg-tag-green text-tag-green-foreground"
        )}
      >
        {icon}
      </span>
      <span className="text-sm text-muted-foreground">{children}</span>
    </div>
  );
}
